import { API, type ResolvedMenuEntry, type UpsertMenuEntryFields } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import type { MenuAudience } from "@swivl-mono/types";
import { Button, ButtonSize, ButtonVariant, Spinner } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Member } from "../../Models/Member/Member.Current";

const AUDIENCES: MenuAudience[] = ["prospect", "tenant", "past-tenant"];

/**
 * Studio Menu admin page — list of `MenuEntry` cards with cascade-aware
 * inheritance affordances (Customize / Revert) plus "+ Add custom entry".
 *
 * Scope is read from the URL: ORG / GROUP / LOCATION. Saved changes affect
 * the next conversation's filtered menu.
 *
 * See docs/snowy/plans/2026-04-28-buttons-kind-plan.md §5 + §7 Stage 5.
 */
export function MenuView(): React.ReactElement {
  const params = useParams<{ groupId?: string; locationId?: string }>();
  const groupId = params.groupId ? parseInt(params.groupId, 10) : undefined;
  const locationId = params.locationId ? parseInt(params.locationId, 10) : undefined;

  const orgId = Member.orgId;
  const [entries, setEntries] = useState<ResolvedMenuEntry[] | null>(null);
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const reload = async (): Promise<void> => {
    if (!orgId) return;
    const fresh = await API.Menu.getEffectiveMenu(orgId, groupId, locationId);
    setEntries(fresh);
    setEditingOptionId(null);
    setShowAdd(false);
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, groupId, locationId]);

  if (!orgId) return <div className="p-4 text-grey-500">No org selected.</div>;
  if (entries === null) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const scopeLabel = locationId ? "this location" : groupId ? "this group" : "the org";

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-grey-800">Menu</h1>
        <p className="mt-1 text-sm text-grey-500">
          The list of operations the bot offers customers across web, voice, and SMS. Editing here applies to {scopeLabel}.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {entries.map((entry) =>
          editingOptionId === entry.optionId ? (
            <EntryEditor
              key={entry.optionId}
              entry={entry}
              orgId={orgId}
              groupId={groupId}
              locationId={locationId}
              onCancel={() => setEditingOptionId(null)}
              onSaved={reload}
            />
          ) : (
            <EntryCard
              key={entry.optionId}
              entry={entry}
              onCustomize={() => setEditingOptionId(entry.optionId)}
              onRevert={async () => {
                if (entry.rowId == null) return;
                if (!confirm(`Revert "${entry.label}" back to the inherited or default value?`)) return;
                await API.Menu.deleteEntry(orgId, entry.rowId);
                await reload();
              }}
            />
          )
        )}
      </div>

      <div className="mt-6">
        {showAdd ? (
          <EntryEditor
            entry={blankCustomEntry()}
            orgId={orgId}
            groupId={groupId}
            locationId={locationId}
            isNew
            onCancel={() => setShowAdd(false)}
            onSaved={reload}
          />
        ) : (
          <Button size={ButtonSize.SMALL} variant={ButtonVariant.DASHED} onClick={() => setShowAdd(true)}>
            + Add custom entry
          </Button>
        )}
      </div>
    </div>
  );
}

interface EntryCardProps {
  entry: ResolvedMenuEntry;
  onCustomize: () => void;
  onRevert: () => Promise<void>;
}

function EntryCard({ entry, onCustomize, onRevert }: EntryCardProps): React.ReactElement {
  return (
    <div className="rounded-lg border border-grey-200 bg-white p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-grey-800">{entry.label}</span>
            <OriginPill origin={entry.origin} />
            {!entry.enabled && <span className="rounded bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700">Disabled</span>}
            {entry.isCustom && <span className="rounded bg-purple-50 px-2 py-0.5 text-[10px] font-medium text-purple-700">Custom</span>}
          </div>
          <div className="mt-1 text-xs text-grey-500">
            <span className="font-mono">{entry.optionId}</span>
            {" · "}
            audiences: {entry.audiences.join(", ")}
            {entry.requires.length > 0 && ` · requires: ${entry.requires.join(", ")}`}
            {entry.forbids.length > 0 && ` · forbids: ${entry.forbids.join(", ")}`}
          </div>
          <div className="mt-2 text-xs text-grey-600">
            <span className="text-grey-400">LLM input:</span> {entry.llmMessage}
          </div>
          {entry.whenToUse && (
            <div className="mt-1 text-xs text-grey-500 italic">
              <span className="text-grey-400">When to use:</span> {entry.whenToUse}
            </div>
          )}
        </div>
        <div className="ml-3 flex flex-col gap-2">
          <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={onCustomize}>
            Customize
          </Button>
          {entry.origin !== "default" && entry.rowId != null && (
            <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={onRevert}>
              {entry.isCustom ? "Delete" : "Revert"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function OriginPill({ origin }: { origin: ResolvedMenuEntry["origin"] }): React.ReactElement {
  const styles: Record<ResolvedMenuEntry["origin"], string> = {
    default: "bg-grey-100 text-grey-600",
    org: "bg-blue-50 text-blue-700",
    group: "bg-teal-50 text-teal-700",
    location: "bg-orange-50 text-orange-700"
  };
  return (
    <span className={filterClasses("rounded px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase", styles[origin])}>
      {origin === "default" ? "Default" : origin === "org" ? "Org" : origin === "group" ? "Group" : "Location"}
    </span>
  );
}

interface EntryEditorProps {
  entry: ResolvedMenuEntry;
  orgId: number;
  groupId?: number;
  locationId?: number;
  isNew?: boolean;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}

function EntryEditor({ entry, orgId, groupId, locationId, isNew = false, onCancel, onSaved }: EntryEditorProps): React.ReactElement {
  const [label, setLabel] = useState(entry.label);
  const [llmMessage, setLlmMessage] = useState(entry.llmMessage);
  const [whenToUse, setWhenToUse] = useState(entry.whenToUse ?? "");
  const [audiences, setAudiences] = useState<MenuAudience[]>(entry.audiences);
  const [requires, setRequires] = useState(entry.requires.join(", "));
  const [forbids, setForbids] = useState(entry.forbids.join(", "));
  const [enabled, setEnabled] = useState(entry.enabled);
  const [optionId, setOptionId] = useState(entry.optionId);
  const [saving, setSaving] = useState(false);

  const toggleAudience = (a: MenuAudience): void => {
    setAudiences((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));
  };

  const handleSave = async (): Promise<void> => {
    if (!label.trim() || !llmMessage.trim() || !optionId.trim() || audiences.length === 0) {
      alert("Label, optionId, llmMessage, and at least one audience are required.");
      return;
    }
    setSaving(true);
    try {
      const fields: UpsertMenuEntryFields = {
        label: label.trim(),
        llmMessage: llmMessage.trim(),
        whenToUse: whenToUse.trim() || undefined,
        audiences,
        requires: parseList(requires),
        forbids: parseList(forbids),
        enabled,
        ...(isNew ? { isCustom: true } : {})
      };
      await API.Menu.upsertEntry(orgId, optionId.trim(), fields, { groupId, locationId });
      await onSaved();
    } catch (e) {
      console.error("Menu upsert failed", e);
      alert("Save failed. Check console.");
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-orange-300 bg-orange-50 p-4">
      <div className="mb-3 text-xs font-medium tracking-wide text-orange-700 uppercase">{isNew ? "New custom entry" : `Editing ${entry.label}`}</div>
      <div className="flex flex-col gap-3">
        {isNew && (
          <Field label="Option ID (kebab-case)" hint="Stable id used internally. Auto-generated from label if empty.">
            <input
              className="w-full rounded border border-grey-300 px-2 py-1 text-sm"
              value={optionId}
              onChange={(e) => setOptionId(e.target.value)}
            />
          </Field>
        )}
        <Field label="Label" hint="What the customer sees on the button (and as the post-tap record).">
          <input
            className="w-full rounded border border-grey-300 px-2 py-1 text-sm"
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
              if (isNew && !optionId) setOptionId(slugify(e.target.value));
            }}
          />
        </Field>
        <Field label="LLM message" hint="What the LLM sees as the user's input on tap. Never displayed.">
          <textarea
            rows={2}
            className="w-full rounded border border-grey-300 px-2 py-1 text-sm"
            value={llmMessage}
            onChange={(e) => setLlmMessage(e.target.value)}
          />
        </Field>
        <Field label="When to use" hint="Optional natural-language hint to the LLM about when to surface this entry.">
          <textarea
            rows={2}
            className="w-full rounded border border-grey-300 px-2 py-1 text-sm"
            value={whenToUse}
            onChange={(e) => setWhenToUse(e.target.value)}
          />
        </Field>
        <Field label="Audiences">
          <div className="flex gap-3">
            {AUDIENCES.map((a) => (
              <label key={a} className="flex items-center gap-1 text-sm text-grey-700">
                <input type="checkbox" checked={audiences.includes(a)} onChange={() => toggleAudience(a)} />
                {a}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Requires (Cortex flag IDs, comma-separated)" hint="Entry surfaces only if all of these flags are true.">
          <input className="w-full rounded border border-grey-300 px-2 py-1 text-sm" value={requires} onChange={(e) => setRequires(e.target.value)} />
        </Field>
        <Field label="Forbids (Cortex flag IDs, comma-separated)" hint="Entry surfaces only if none of these flags are true.">
          <input className="w-full rounded border border-grey-300 px-2 py-1 text-sm" value={forbids} onChange={(e) => setForbids(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm text-grey-700">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled
        </label>
      </div>
      <div className="mt-4 flex gap-2">
        <Button size={ButtonSize.SMALL} variant={ButtonVariant.PRIMARY} onClick={handleSave} disabled={saving}>
          {saving ? <Spinner /> : "Save"}
        </Button>
        <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
}

function Field({ label, hint, children }: FieldProps): React.ReactElement {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-grey-700">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-grey-500">{hint}</span>}
    </label>
  );
}

function blankCustomEntry(): ResolvedMenuEntry {
  return {
    optionId: "",
    label: "",
    llmMessage: "",
    whenToUse: "",
    audiences: ["prospect"],
    requires: [],
    forbids: [],
    enabled: true,
    isCustom: true,
    origin: "org"
  };
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function parseList(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}
